<template>
    <v-card>
        <v-card-title class="text-h5 bg-grey-lighten-2" primary-title>
            {{ existingSOP ? `Edit Sample SOP ${existingSOP.code}` : "Create Sample SOP" }}
            <v-spacer></v-spacer>
        </v-card-title>
        <v-card-text>
            <v-form @submit.prevent="save" v-model="isFormValid" ref="form">
                <v-row>
                    <v-col cols="8">
                        <v-text-field
                            label="Name"
                            v-model="name"
                            density="comfortable"
                            :rules="[(value) => !!value || 'Name is required']"
                        />
                    </v-col>
                </v-row>
                <v-row>
                    <!-- in a later phase, we'll probably have the backend create the Confluence page
                         so the user won't be supplying this in the form -->
                    <v-col cols="12">
                        <v-text-field
                            label="Description URL"
                            v-model="descriptionUrl"
                            density="comfortable"
                        />
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="6">
                        <v-autocomplete
                            label="Sample Type"
                            v-model="sampleType"
                            :items="materialTypes"
                            item-title="name"
                            item-value="id"
                            required
                            :rules="[(value) => !!value || 'Sample type is required']"
                        ></v-autocomplete>
                    </v-col>

                    <v-col cols="6">
                        <v-autocomplete
                            label="Sample Descriptors"
                            v-model="descriptors"
                            :items="sampleDescriptors"
                            item-title="name"
                            item-value="id"
                            multiple
                            chips
                        ></v-autocomplete>
                    </v-col>
                </v-row>
            </v-form>
        </v-card-text>
        <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn @click="cancel" color="primary">Cancel</v-btn>
            <v-btn @click="save" color="primary" :disabled="!isFormValid">Save</v-btn>
        </v-card-actions>
    </v-card>
</template>

<script setup lang="ts">
import { ref } from "vue";
import { MaterialSampleSOP, MaterialType, SampleDescriptor, samplesAPI, SavedMaterialSampleSOP } from "@/services/samples/samplesAPI";
import { withLoadingScreen } from "@/lib/loading";

const { existingSOP, materialTypes } = defineProps<{
    existingSOP?: SavedMaterialSampleSOP;
    materialTypes: MaterialType[];
    sampleDescriptors: SampleDescriptor[];
}>();

const emit = defineEmits<{
    (e: "canceled"): void;
    (e: "samplesSOPSaved", savedSOP: MaterialSampleSOP): void;
}>();

const name = ref(existingSOP ? existingSOP.name : "");
const descriptionUrl = ref(existingSOP ? existingSOP.descriptionUrl : "");
const sampleType = ref<string>(existingSOP ? existingSOP.materialTypeId : "");
const descriptors = ref<string[]>(existingSOP ? [...existingSOP.sampleDescriptorIds] : []);
const isFormValid = ref(false);

function cancel() {
    // reset form fields to existing SOP values or empty if no existing SOP
    name.value = existingSOP ? existingSOP.name : "";
    descriptionUrl.value = existingSOP ? existingSOP.descriptionUrl : "";
    sampleType.value = existingSOP ? existingSOP.materialTypeId : "";
    descriptors.value = existingSOP ? existingSOP.sampleDescriptorIds : [];
    isFormValid.value = false;
    emit("canceled");
}

async function save() {
    const sop = {
        name: name.value,
        descriptionUrl: descriptionUrl.value,
        materialTypeId: sampleType.value,
        sampleDescriptorIds: descriptors.value,
    };
    await withLoadingScreen(async () => {
        if (existingSOP) {
            await samplesAPI.updateSampleSOP(existingSOP.id, sop);
        } else {
            await samplesAPI.createSampleSOP(sop);
        }
    });
    emit("samplesSOPSaved", sop);
}
</script>
